import * as React from "react";
import {
    makeStyles,
    mergeClasses,
    SpinButton,
    SpinButtonOnChangeData,
    SpinButtonProps,
    tokens,
} from "@fluentui/react-components";
import { useTheme } from "../../hooks/useTheme";

const useStyles = makeStyles({
    // Same hover outline as NewformaInput. SpinButton draws its border on the root's ::before
    // pseudo-element, and the filled appearances make it transparent on hover, so override there.
    spinButtonHover: {
        "&&&:hover::before": {
            "border-color": tokens.colorNeutralStrokeAccessible,
        },
    },
});

export interface NewformaSpinButtonProps {
    value: number;
    /** Lower bound (inclusive). Spun or typed values below this are clamped up to it. */
    min?: number;
    /** Upper bound (inclusive). Spun or typed values above this are clamped down to it. */
    max?: number;
    step?: number;
    disabled?: boolean;
    className?: string;
    size?: SpinButtonProps["size"];
    dataTestId?: string;
    "aria-label"?: string;
    /** Called with the clamped integer whenever the user spins or commits a typed value. */
    onChange: (value: number) => void;
}

function clamp(value: number, min: number | undefined, max: number | undefined): number {
    let result = value;
    if (min !== undefined && result < min) {
        result = min;
    }
    if (max !== undefined && result > max) {
        result = max;
    }
    return result;
}

export const NewformaSpinButton: React.FC<NewformaSpinButtonProps> = ({
    value,
    min,
    max,
    step,
    disabled,
    className,
    size,
    dataTestId,
    "aria-label": ariaLabel,
    onChange,
}) => {
    const styles = useStyles();
    const { isDarkMode } = useTheme();

    const handleChange = React.useCallback(
        (_event: React.SyntheticEvent | React.ChangeEvent, data: SpinButtonOnChangeData) => {
            let next: number | undefined;
            if (data.value !== undefined && data.value !== null) {
                // spin button / arrow key. `step` only sets the increment, so normalize to a finite integer here.
                next = Number.isFinite(data.value) ? Math.trunc(data.value) : undefined;
            } else if (data.displayValue !== undefined) {
                // typed text committed on blur or Enter
                const parsed = parseInt(data.displayValue, 10);
                next = Number.isNaN(parsed) ? undefined : parsed;
            }

            // Unparseable or non-finite input falls back to the current value so the control re-renders a valid number.
            onChange(clamp(next ?? value, min, max));
        },
        [onChange, value, min, max]
    );

    return (
        <SpinButton
            // "filled-darker" fills the field with colorNeutralBackground3 in dark mode, matching the other inputs.
            appearance={isDarkMode ? "filled-darker" : "outline"}
            value={value}
            min={min}
            max={max}
            step={step}
            disabled={disabled}
            className={mergeClasses(!disabled && styles.spinButtonHover, className)}
            size={size}
            onChange={handleChange}
            data-testid={dataTestId}
            aria-label={ariaLabel}
        />
    );
};
